'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function HeroSection() {
  return (
    <section
      className="hero-bg relative overflow-hidden pt-28 pb-20 px-4 sm:px-6"
      aria-labelledby="hero-heading"
    >
      {/* Decorative blobs — hidden from AT, no interaction */}
      <div
        className="absolute top-20 right-[8%] w-72 h-72 rounded-full pointer-events-none bg-[radial-gradient(circle,rgba(232,68,90,0.06)_0%,transparent_70%)] blur-[40px]"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-10 left-[5%] w-56 h-56 rounded-full pointer-events-none bg-[radial-gradient(circle,rgba(255,122,138,0.05)_0%,transparent_70%)] blur-[30px]"
        aria-hidden="true"
      />

      <div className="max-w-5xl mx-auto text-center relative z-10">
        {/* Trust badge */}
        <p
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 bg-primary-light border-[1.5px] border-primary-mid"
          aria-label="Service highlights: 100% free, 100% secure, no login required"
        >
          <span className="w-2 h-2 rounded-full animate-pulse bg-primary" aria-hidden="true" />
          <span className="text-primary text-[12px] font-heading font-bold tracking-[0.08em] uppercase">
            ✓ 100% Free · ✓ 100% Secure · ✓ No Login
          </span>
        </p>

        {/* H1: primary keyword front-loaded */}
        <h1
          id="hero-heading"
          className="font-heading font-extrabold tracking-tight mb-5 text-[clamp(40px,7vw,72px)] text-brand-dark leading-[1.05]"
        >
          Free PDF Tools{' '}
          <span className="bg-[linear-gradient(135deg,#E8445A_0%,#FF7A8A_60%,#E8445A_100%)] bg-clip-text text-transparent">
            You Can Trust
          </span>
        </h1>

        {/* Subheading: updated to reflect 9 tools including Sign PDF */}
        <p className="mx-auto mb-10 leading-relaxed text-brand-mid text-[clamp(16px,2.5vw,20px)] max-w-[560px] font-body font-normal">
          Merge, split, sign, compress, and edit PDFs online.
          <span className="block mt-1">No uploads. No storage. 100% private.</span>
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-14">
          <Link
            href="/merge-pdf"
            className="btn-primary flex items-center gap-2 text-base px-8 py-3.5 !bg-primary text-white rounded-[100px] font-heading font-bold !text-[15px] no-underline"
            style={{
              boxShadow: '0 6px 24px rgba(232,68,90,0.28)',
              transition: 'all 0.2s ease',
            }}
          >
            <Icon name="DocumentPlusIcon" size={18} variant="solid" aria-hidden="true" />
            <span>Merge PDF Files Free</span>
          </Link>
          <Link
            href="#tools"
            className="btn-secondary flex items-center gap-2 text-base bg-white text-brand-mid border-[1.5px] border-brand-border dark:bg-brand-mid dark:text-white dark:hover:text-primary dark:border-brand-border hover:border-brand-border rounded-[100px] font-heading font-semibold text-[15px] px-7 py-3.5 no-underline"
            style={{ transition: 'all 0.2s ease' }}
          >
            <span>Explore All PDF Tools</span>
            <Icon name="ArrowRightIcon" size={16} aria-hidden="true" />
          </Link>
        </div>

        {/* Feature highlights: float-gentle keyframe defined once in layout.tsx */}
        <div className="flex flex-wrap items-center justify-center gap-3" role="list">
          {[
            { icon: 'ShieldCheckIcon', value: '100% Private', desc: 'Privacy guaranteed' },
            { icon: 'LockClosedIcon', value: '256-bit SSL', desc: 'Bank-level encryption' },
            { icon: 'TrashIcon', value: 'Auto-Deleted', desc: 'Files wiped instantly' },
            { icon: 'HeartIcon', value: 'Always Free', desc: 'No paid tiers' },
          ].map((stat, i) => (
            <div
              key={stat.value}
              className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white border-[1.5px] border-brand-border shadow-[0_2px_12px_rgba(26,26,46,0.05)]"
              role="listitem"
              style={{
                animation: `float-gentle ${3.5 + i * 0.4}s ease-in-out infinite`,
              }}
            >
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary-light"
                aria-hidden="true"
              >
                <Icon name={stat.icon as any} size={14} variant="solid" className="text-primary" />
              </div>
              <div className="text-left">
                <div className="font-heading font-extrabold text-[13px] text-brand-dark">
                  {stat.value}
                </div>
                <div className="font-body text-[10px] text-brand-muted">{stat.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
